/**
 * 后端原始 DTO 类型（数字枚举）→ 归一化转换
 */
import type {
  Article,
  AuthResponse,
  Comment,
  Conversation,
  Event,
  LeaderboardEntry,
  Match,
  Message,
  Notification,
  Paged,
  Player,
  PlayerBindingRequest,
  PlayerDetail,
  PlayerEvent,
  PlayerGameRecord,
  PlayerStatsRadar,
  RuleRevision,
  Subscription,
  Team,
  TeamBlacklist,
  TeamEvent,
  TeamInvite,
  User,
  UserAchievement,
  UserSubscriptionStatus,
} from './types'
import {
  normalizeBindingStatus,
  normalizeBindingType,
  normalizeEventStatus,
  normalizeEventType,
  normalizePlayerType,
  normalizeRegistrationMode,
  normalizeRegistrationStatus,
  normalizeUserGroup,
  normalizeUserRole,
} from './enums'

// ==================== 原始类型（与后端 JSON 一一对应，枚举为数字） ====================

interface RawUser {
  id: string
  email: string
  fullName: string
  role: number
  roleDisplayName: string
  roleName: string
  createdAt: string
  updatedAt?: string | null
  isActive: boolean
  avatarUrl?: string | null
  teamId?: string | null
  displayTeamId?: string | null
  ownedTeamId?: string | null
  emailCredits: number
  userGroup: number
  userGroupName: string
  userGroupDisplayName: string
  subscriptionExpireTime?: string | null
  isSubscribed: boolean
}

interface RawPlayer {
  id: string
  name: string
  gameId?: string | null
  gameRank?: string | null
  description?: string | null
  playerType: number
  createdAt: string
  updatedAt: string
  teamId?: string | null
  customGameData?: string | null
  xuexinVerificationCode?: string | null
}

interface RawTeam {
  id: string
  name: string
  description?: string | null
  qqNumber?: string | null
  likes: number
  createdAt: string
  updatedAt: string
  logoUrl?: string | null
  hidePlayers: boolean
  players: RawPlayer[]
  hasDispute: boolean
  disputeDetail?: string | null
  communityPostId?: string | null
  ratingAverage: number
  ratingCount: number
  reviews?: unknown[] | null
}

interface RawTeamEvent {
  teamId: string
  eventId: string
  teamName: string
  eventName?: string | null
  registrationTime: string
  status: number
  notes?: string | null
  registeredByUserId?: string | null
  qqNumberMasked?: string | null
  qqNumberFull?: string | null
  teamHasDispute: boolean
  teamDisputeDetail?: string | null
  teamCommunityPostId?: string | null
  teamRatingAverage: number
  teamRatingCount: number
  isWaitlist: boolean
}

interface RawEvent {
  id: string
  name: string
  description?: string | null
  qqGroup?: string | null
  rulesMarkdown?: string | null
  registrationStartTime: string
  registrationEndTime: string
  competitionStartTime: string
  competitionEndTime?: string | null
  maxTeams?: number | null
  waitlistTeams?: number | null
  status: number
  createdAt: string
  updatedAt: string
  createdByUserId?: string | null
  logoUrl?: string | null
  backgroundUrl?: string | null
  championTeamId?: string | null
  championTeamName?: string | null
  registeredTeamsCount: number
  registeredTeams?: RawTeamEvent[] | null
  adminUserIds?: string[] | null
  customData?: string | null
  registrationMode: number
  prizesJson?: string | null
  requiredQqGroup?: string | null
  enforceGroupCheck: boolean
  requirePlayerProfileScreenshots: boolean
  eventType: number
}

interface RawMatch {
  id: string
  homeTeamId: string
  homeTeamName: string
  awayTeamId: string
  awayTeamName: string
  matchTime: string
  eventId: string
  eventName: string
  liveLink?: string | null
  stage?: string | null
  winnerTeamId?: string | null
  winnerTeamName?: string | null
  replayLink?: string | null
  customData: string
  commentator?: string | null
  director?: string | null
  referee?: string | null
  likes: number
  createdAt: string
  updatedAt: string
  isToday: boolean
  homeTeamLogo?: string | null
  awayTeamLogo?: string | null
}

interface RawArticle {
  id: string
  title: string
  contentMarkdown: string
  authorUserId: string
  authorName: string
  authorTeamId?: string | null
  authorTeamName?: string | null
  honors?: RawEvent[] | null
  createdAt: string
  updatedAt: string
  likes: number
  views: number
  commentsCount: number
}

interface RawComment {
  id: string
  articleId: string
  content: string
  authorUserId: string
  authorName: string
  authorEmail: string
  authorAvatarUrl?: string | null
  parentId?: string | null
  replies?: RawComment[] | null
  createdAt: string
}

// ==================== 归一化函数 ====================

export function normalizeUser(u: any): User {
  return {
    ...u,
    role: normalizeUserRole(u.role) ?? 'None',
    userGroup: normalizeUserGroup(u.userGroup) ?? 'Free',
  }
}

export function normalizePlayer(p: any): Player {
  return { ...p, playerType: normalizePlayerType(p.playerType) }
}

export function normalizeTeam(t: any): Team {
  return {
    ...t,
    players: (t.players || []).map(normalizePlayer),
    reviews: t.reviews as Team['reviews'],
  }
}

export function normalizeTeamEvent(te: any): TeamEvent {
  return { ...te, status: normalizeRegistrationStatus(te.status) ?? 'Pending' }
}

export function normalizeEvent(e: any): Event {
  return {
    ...e,
    status: normalizeEventStatus(e.status) ?? 'Draft',
    registrationMode: normalizeRegistrationMode(e.registrationMode) ?? 'Team',
    eventType: normalizeEventType(e.eventType) ?? 'Public',
    registeredTeams: e.registeredTeams?.map(normalizeTeamEvent) ?? null,
  }
}

export function normalizePlayerEvent(pe: any): PlayerEvent {
  return { ...pe, status: normalizeRegistrationStatus(pe.status) ?? 'Pending' }
}

export function normalizeMatch(m: any): Match {
  return m as Match
}

export function normalizeArticle(a: any): Article {
  return { ...a, honors: a.honors?.map(normalizeEvent) ?? null }
}

export function normalizeComment(c: any): Comment {
  return { ...c, replies: c.replies?.map(normalizeComment) ?? null }
}

export function normalizeNotification(n: Notification): Notification {
  return n
}

export function normalizeConversation(c: Conversation): Conversation {
  return c
}

export function normalizeMessage(m: Message): Message {
  return m
}

export function normalizeLeaderboardEntry(e: LeaderboardEntry): LeaderboardEntry {
  return e
}

export function normalizeUserAchievement(a: UserAchievement): UserAchievement {
  return a
}

export function normalizeRuleRevision(r: RuleRevision): RuleRevision {
  return r
}

export function normalizeTeamInvite(i: TeamInvite): TeamInvite {
  return i
}

export function normalizePlayerBinding(b: PlayerBindingRequest): PlayerBindingRequest {
  return {
    ...b,
    requestType: normalizeBindingType(b.requestType) ?? 'CaptainInvite',
    status: normalizeBindingStatus(b.status) ?? 'Pending',
  }
}

export function normalizePlayerDetail(d: PlayerDetail): PlayerDetail {
  return { ...d, playerType: normalizePlayerType(d.playerType) }
}

export function normalizeSubscription(s: Subscription): Subscription {
  return { ...s, tier: normalizeUserGroup(s.tier) ?? 'Free' }
}

export function normalizeSubscriptionStatus(s: UserSubscriptionStatus): UserSubscriptionStatus {
  return { ...s, currentGroup: normalizeUserGroup(s.currentGroup) ?? 'Free' }
}

export function normalizeTeamBlacklist(b: TeamBlacklist): TeamBlacklist {
  return b
}

export function normalizeAuthResponse(r: any): AuthResponse {
  return { ...r, user: normalizeUser(r.user) }
}

export function normalizePaged<T>(p: { items: T[]; totalCount: number; page: number; pageSize: number; totalPages?: number }): Paged<T> {
  const totalPages = p.totalPages ?? (p.pageSize > 0 ? Math.ceil(p.totalCount / p.pageSize) : 0)
  return { items: p.items, totalCount: p.totalCount, page: p.page, pageSize: p.pageSize, totalPages }
}

/** 排行榜接口返回普通数组（非分页对象） */
export function normalizeLeaderboardList(list: LeaderboardEntry[]): LeaderboardEntry[] {
  return list
}

export function normalizeRadar(r: PlayerStatsRadar): PlayerStatsRadar {
  return r
}

export function normalizePlayerGameRecord(r: PlayerGameRecord): PlayerGameRecord {
  return r
}
